import type { HTMLAttributes } from "react";
import { Box, Stack, Typography } from "@mui/material";
import type { TextSegment } from "@domain/Search";
import { JobStatusChip } from "@gui/widgets/JobStatusChip";
import type { JobLookupRow } from "./JobLookupSearch.vm";

interface JobLookupOptionProps extends HTMLAttributes<HTMLLIElement> {
	result: JobLookupRow;
}

// A tint of the palette colour, so it reads in both schemes with the text's own colour on it.
const MATCH_SX = {
	bgcolor: "rgba(var(--mui-palette-primary-mainChannel) / 0.28)",
	color: "text.primary",
	borderRadius: 1,
} as const;

/** Text with what the search matched picked out in a colour. */
const MatchedText = ({ parts }: { parts: TextSegment[] }) =>
	parts.map((part, index) =>
		part.match ? (
			<Box key={index} component="mark" sx={MATCH_SX}>
				{part.text}
			</Box>
		) : (
			part.text
		),
	);

/** One row of the job box's dropdown: the job's title and status, then where it sits. */
export function JobLookupOption({ result, ...optionProps }: JobLookupOptionProps) {
	return (
		<li {...optionProps}>
			<Stack sx={{ flex: 1, minWidth: 0 }}>
				<Stack direction="row" spacing={1} sx={{ alignItems: "center", justifyContent: "space-between" }}>
					<Typography variant="body2" noWrap>
						<MatchedText parts={result.titleParts} />
					</Typography>
					{result.status && <JobStatusChip status={result.status} compact sx={{ flexShrink: 0 }} />}
				</Stack>
				{result.subtitle && (
					<Typography variant="caption" color="text.secondary" noWrap>
						<MatchedText parts={result.subtitleParts} />
					</Typography>
				)}
			</Stack>
		</li>
	);
}
